import { EVENT_STATUS } from '@hedepy/common/enums/EVENT'
import dayjs, { type Dayjs } from 'dayjs'
import { useRouter } from 'next/router'
import React, { Suspense, useEffect, useRef, useState } from 'react'

import ServiceDropdownMultibooking from '@/legacy-src/components/calendars/client/multibooking/ServiceDropdownMultibooking'
import Loader from '@/legacy-src/components/Loader'
import useRebookEvent from '@/legacy-src/components/reservation/hooks/useRebookEvent'
import useEvents from '@/legacy-src/hooks/useEvents'
import { useMediaQuery } from '@/legacy-src/hooks/useMediaQuery'
import useServices from '@/legacy-src/hooks/useServices'
import useTherapist from '@/legacy-src/hooks/useTherapist'
import useUser from '@/legacy-src/hooks/useUser'
import { cn } from '@/legacy-src/lib/utils'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'
import { tailwindConfig } from '@/legacy-src/utilities/tailwind'

import { ClientBookingFreeSlotTypeFilter } from '@/src/domains/client-booking/components/ClientBookingFreeSlotTypeFilter'
import { ClientBookingRebookingNotification } from '@/src/domains/client-booking/components/ClientBookingRebookingNotification'
import { ClientBookingSubscriptionStatus } from '@/src/domains/client-booking/components/ClientBookingSubscriptionStatus'
import { useClientBookingFreeSlots } from '@/src/domains/client-booking/hooks/useClientBookingFreeSlots'
import { useClientBookingRebookFreeSlotTypeFilter } from '@/src/domains/client-booking/hooks/useClientBookingRebookFreeSlotTypeFilter'
import { useFreeSlotTypeQueryFilterWithAvailability } from '@/src/domains/client-booking/hooks/useFreeSlotTypeQueryFilterWithAvailability'
import { getFirstFreeDayOfMonth } from '@/src/domains/client-booking/utils/getFirstFreeDayOfMonth'

import CalendarMonthView from './CalendarMonthView'
import { CompanyBenefitsLeft } from './CompanyBenefitsNotifications'
import SlotsForDate from './SlotsForDate'
import { SubscriptionDiffersInPrice } from './SubscriptionNotifications'

type Props = {
  therapist: TherapistType
  service: ServiceType
  showSendMessage?: boolean
}

const useSetPreselectedServiceForTherapist = (therapistId: number) => {
  const router = useRouter()
  const { therapist } = useTherapist(therapistId)
  const { isClient } = useUser()
  const { services } = useServices()
  const { events } = useEvents(isClient ? 'all' : 'current')

  const serviceFromQuery = router.query.service as string

  const setServiceId = (serviceId: number) => {
    router.replace({
      pathname: router.pathname,
      query: { ...router.query, service: serviceId },
    })
  }

  useEffect(() => {
    if (!events || !services) return

    const bookableServiceIds = therapist?.services?.filter((serviceId) => services.some((s) => s.id === serviceId))
    // the first valid service for the therapist as they are ordered from backend
    const defaultTherapistServiceId = bookableServiceIds?.[0]

    // wait for the therapist services to be loaded
    if (!bookableServiceIds || !defaultTherapistServiceId) return

    // if service is set and available, do nothing
    if (serviceFromQuery && bookableServiceIds.includes(parseInt(serviceFromQuery, 10))) return

    const serviceIdFromLastBoughtEventWithTherapist = events
      .filter(
        (e) => e.therapistId === therapistId && [EVENT_STATUS.RESERVED, EVENT_STATUS.COMPLETED].includes(e.status),
      )
      .sort((a, b) => dayjs(b.startDate).diff(dayjs(a.startDate)))[0]?.serviceId

    if (
      serviceIdFromLastBoughtEventWithTherapist &&
      // potentially the therapist could have stopped offering the service
      bookableServiceIds.includes(serviceIdFromLastBoughtEventWithTherapist)
    ) {
      setServiceId(serviceIdFromLastBoughtEventWithTherapist)
    } else if (defaultTherapistServiceId) {
      setServiceId(defaultTherapistServiceId)
    }
  }, [serviceFromQuery, services, events, therapist])
}

const ClientCalendarMultibooking: React.FC<Props> = ({ therapist, service, showSendMessage = false }) => {
  const { filter } = useFreeSlotTypeQueryFilterWithAvailability({ therapist })
  const rebookFilter = useClientBookingRebookFreeSlotTypeFilter()

  const type = rebookFilter ?? filter

  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'))
  const [day, setDay] = useState<Dayjs | null>(null)
  const daySearchCounter = useRef(0)
  const slotsForDateRef = useRef<HTMLDivElement>(null)

  const { isRebookingInProgress } = useRebookEvent()

  const { slots } = useClientBookingFreeSlots({
    therapistId: therapist.therapistId,
    serviceId: service.id,
    type,
    dateTo: month.endOf('month').endOf('day').unix(),
  })

  const shownDay = !day || day.isSame(month, 'month') ? day : (getFirstFreeDayOfMonth(slots ?? [], month) ?? month)

  const isTablet = useMediaQuery(`(min-width: ${tailwindConfig.theme.screens['tablet']})`)

  useSetPreselectedServiceForTherapist(therapist.therapistId)

  useEffect(() => {
    // If still loading slots or day has already been selected, do nothing
    if (slots === undefined || day) return

    const sortedSlots = slots.sort((a, b) => (dayjs(a.startDate).isBefore(b.startDate) ? -1 : 1))

    // If slots were found, set day to the one with earliest slot
    if (sortedSlots[0]) {
      const newDay = dayjs(sortedSlots[0].startDate)
      const newMonth = newDay.startOf('month')

      setDay(newDay)
      setMonth(newMonth)

      // If no slots were found and we haven't searched enough times, increment month and counter, and repeat
    } else if (daySearchCounter.current < 3) {
      const newMonth = month.add(1, 'month').startOf('month')

      setMonth(newMonth)
      daySearchCounter.current += 1

      // Otherwise, fail silently by setting day and month to current ones
    } else {
      const newDay = dayjs()
      const newMonth = newDay.startOf('month')

      setMonth(newMonth)
      setDay(newDay)
    }
  }, [day, month, slots])

  useEffect(() => {
    const newMonth = dayjs().startOf('month')

    setMonth(newMonth)
    setDay(null)
    daySearchCounter.current = 0
  }, [therapist.therapistId, service.id, type])

  useEffect(() => {
    // Scroll to show calendar section when day is selected (only scrolls if needed)
    if (day && slotsForDateRef.current) {
      setTimeout(() => {
        slotsForDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 0)
    }
  }, [day])

  return (
    <Suspense fallback={<Loader loaderType="section-large" visible />}>
      <div className="relative mt-5 pb-6 tablet:mb-12 tablet:mt-10" ref={slotsForDateRef}>
        {isTablet ? <ClientBookingSubscriptionStatus month={month} therapist={therapist} service={service} /> : null}
        <ClientBookingRebookingNotification />

        <Loader loaderType="section-large" visible={!day} />

        {isRebookingInProgress ? null : (
          <div className="mb-5 flex w-full flex-col gap-3 tablet:mx-auto tablet:w-fit tablet:flex-row">
            <ServiceDropdownMultibooking className="min-w-80" {...{ therapist }} />
            <ClientBookingFreeSlotTypeFilter className="min-w-80" {...{ therapist }} />
          </div>
        )}

        <div className={cn('pt-6 tablet:flex', day ? 'opacity-100' : 'opacity-0')}>
          <div className="border-0 border-b-2 border-solid border-sand-400 tablet:w-1/2 tablet:border-b-0 tablet:border-r-2 tablet:pr-6 laptop:pr-12">
            <CompanyBenefitsLeft serviceId={service.id} />
            {isTablet ? null : (
              <>
                <ClientBookingSubscriptionStatus month={month} therapist={therapist} service={service} />
                <SubscriptionDiffersInPrice month={month} therapist={therapist} service={service} />
              </>
            )}

            <CalendarMonthView
              month={month}
              setMonth={setMonth}
              day={shownDay}
              setDay={setDay}
              therapistId={therapist.therapistId}
              serviceId={service.id}
              type={type}
            />
          </div>
          <div className="mt-5 flex grow flex-col tablet:mt-0 tablet:w-1/2 tablet:p-0 tablet:pl-6 laptop:pl-12">
            {isTablet ? <SubscriptionDiffersInPrice month={month} therapist={therapist} service={service} /> : null}
            <SlotsForDate
              type={type}
              day={shownDay}
              therapist={therapist}
              service={service}
              showSendMessage={showSendMessage}
            />
          </div>
        </div>
      </div>
    </Suspense>
  )
}

export default ClientCalendarMultibooking
